// apps/customer-ui/src/app/core/services/error-handler.service.ts

import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  BaseErrorHandlerService,
  ErrorData,
  ErrorStorageService,
} from '@awo-care/shared-core';
import { UserService } from './user.service';
import { versionEmployee } from 'package.json';
import { environment } from '@awo-care/shared-auth';
import { chunkErrorKey, chunkErrorMessage, reloadOnce } from '@awo-care/utils';

@Injectable({ providedIn: 'root' })
export class EmployeeErrorHandlerService extends BaseErrorHandlerService {
  private userService = inject(UserService);
  private errorStorage = inject(ErrorStorageService);
  private document = inject(DOCUMENT);

  /**
   * Nach einem Deploy fehlende Dateien: einmal still neu laden und damit die
   * neue Version holen, statt einen Fehler zu melden (#1403, ersetzt den
   * Service Worker mit „Update verfügbar“). Mit angefangenem Formular lädt
   * `reloadOnce` nicht neu.
   */
  override async handleError(error: unknown): Promise<void> {
    const win = this.document.defaultView;
    const chunkError = chunkErrorMessage(error);
    if (win && chunkError && reloadOnce(win, chunkErrorKey(chunkError))) {
      return;
    }
    return super.handleError(error);
  }

  getUserId(): string {
    return this.userService.currentUser()?.uid || 'unknown';
  }

  getUserEmail(): string {
    return this.userService.currentUser()?.email || 'unknown';
  }

  getAppVersion(): string {
    return versionEmployee;
  }

  getEnvironment(): string {
    return environment.tier || 'unknown';
  }

  getAppType(): 'customer' | 'employee' {
    return 'employee';
  }

  protected async processError(
    sendErrorData: ErrorData,
    errorData: GlobalError
  ): Promise<void> {
    switch (errorData.bugReport) {
      case 'auto':
        await this.errorStorage.storeError(sendErrorData);
        break;
      case 'manual':
        this.showErrorSnackbar(errorData);
        break;
      case 'none':
        break;
      default:
        if (this.getEnvironment() === 'PROD') {
          await this.errorStorage.storeError(sendErrorData);
        }
        break;
    }
  }
}
